/**
 * src/v2/services/ruleAuthorization.ts
 *
 * Who may manage an execution rule.
 *
 * ## Managing a rule and executing one are different questions
 *
 * A visitor standing in a building executes its rule. That is the whole
 * point of the rule existing, and it requires no authority at all beyond
 * whatever the rule itself checks. Changing what the rule *does* is a
 * different act with a different answer. Nothing here is consulted on the
 * execute path, and nothing on the execute path should start consulting it.
 *
 * ## Ownership is resolved backwards, through the object
 *
 * A GeoLink execution rule has no `spatial_object_id`. It records who
 * created it and nothing about where it applies. But spatial objects carry
 * `linked_execution_rule_id`, and GeoLink can filter on that — so the owning
 * object is one query in the other direction. Authority then comes from
 * `can_manage_rules` on that object, which is the contract's own answer.
 *
 * This is a reverse lookup and it is not free, which is why we have asked
 * GeoLink for a forward key. Until then the lookup is cached per rule id.
 *
 * ## An unlinked rule falls back, visibly
 *
 * A rule that no object points at has no object to authorize through. Those
 * stay with their creator, reported as `creator_legacy`, so the gap is
 * labelled in the UI rather than mistaken for a chain-backed decision.
 */

import { geoLinkSpatialObjectsAdminClient } from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import { getObjectAuthorization } from './authorizationService';
import type { AuthoritySource, ObjectAuthorization } from './authorizationModel';

export interface RuleAuthorization {
  ruleId: string;
  /** The object whose authority governs this rule, when one points at it. */
  objectId: string | null;
  objectName: string | null;
  /** Whether the caller may create, edit, delete or toggle this rule. */
  canManageRule: boolean;
  authoritySources: AuthoritySource[];
  /** Empty when allowed; otherwise the sentence explaining the refusal. */
  reason: string;
  /**
   * True when the answer came from stored provenance because no object
   * pointed at the rule. Surface it — it is the honest statement that this
   * particular decision is not yet chain-backed.
   */
  legacy: boolean;
  /** The underlying object answer, for an authority panel. */
  objectAuthorization: ObjectAuthorization | null;
}

/** The ownership fields GeoLink has stamped on rules over time. */
export interface RuleOwnershipFields {
  id: number | string;
  created_by_public_key?: string | null;
  public_key?: string | null;
  wallet_public_key?: string | null
  owner_public_key?: string | null;
}

/**
 * The wallet recorded as having created a rule.
 *
 * Prefers `created_by_public_key`; the other three are back-compat for rows
 * written before that column existed. Returns '' when none is recorded,
 * which denies — an unattributed rule is not everyone's rule.
 */
export function ruleCreatorWallet(rule: RuleOwnershipFields): string {
  return (
    rule.created_by_public_key
    || rule.public_key
    || rule.wallet_public_key
    || rule.owner_public_key
    || ''
  ).trim();
}

// ── Rule → object resolution ─────────────────────────────────────────────────

/**
 * Cache of rule id to owning object id.
 *
 * Session-lived and never negatively expired, because the link changes only
 * when someone edits a spatial object's rule selection — and that path
 * calls `clearRuleObjectCache`. `null` is cached too: most rules are
 * unlinked, and re-asking GeoLink for every one of them on every render
 * would be the dominant cost here.
 */
const objectByRule = new Map<string, { objectId: string | null; objectName: string | null }>();

export function clearRuleObjectCache(): void {
  objectByRule.clear();
}

/**
 * The spatial object that links to this rule, if any.
 *
 * Takes the first match. More than one object may point at the same rule —
 * a rule is reusable — and in that case authority through any of them is
 * enough, so the ambiguity does not change the answer for a wallet with
 * authority over one.
 */
export async function findObjectForRule(
  ruleId: number | string,
): Promise<{ objectId: string | null; objectName: string | null }> {
  const key = String(ruleId);
  const cached = objectByRule.get(key);
  if (cached) return cached;

  let result: { objectId: string | null; objectName: string | null } = {
    objectId: null,
    objectName: null,
  };
  try {
    const page = await geoLinkSpatialObjectsAdminClient.listObjectsPage({
      linked_execution_rule_id: key,
      limit: 1,
    });
    const row = page.rows?.[0];
    if (row?.id) result = { objectId: row.id, objectName: row.name ?? null };
  } catch {
    // A GeoLink failure must not silently become "no object", because that
    // would downgrade a chain-backed rule to creator ownership. It is left
    // uncached so the next attempt retries, and the caller sees the
    // `legacy` flag it would otherwise not have set.
    return result;
  }

  objectByRule.set(key, result);
  return result;
}

// ── Authorization ────────────────────────────────────────────────────────────

/**
 * Whether `wallet` may manage the rule.
 *
 * Managing requires more than the permission bit. The contract's
 * `can_manage_rules` already folds in the object being active, the binding
 * being approved (or inherited from an approved ancestor) and the
 * organization being cleared to operate — it returns false if any of those
 * fails. Those conditions are re-read from `context` here only to say
 * *which* one refused, because "not authorized" is not a useful thing to
 * tell someone who owns the building.
 */
export async function getRuleAuthorization(
  rule: RuleOwnershipFields,
  wallet: string | null | undefined,
): Promise<RuleAuthorization> {
  const ruleId = String(rule.id);
  const deny = (reason: string, legacy = false): RuleAuthorization => ({
    ruleId,
    objectId: null,
    objectName: null,
    canManageRule: false,
    authoritySources: [],
    reason,
    legacy,
    objectAuthorization: null,
  });

  if (!wallet) return deny('Connect a wallet to manage execution rules.');

  const { objectId, objectName } = await findObjectForRule(ruleId);

  if (!objectId) {
    // No object points at this rule, so there is nothing to authorize
    // through. Creator ownership, named as such.
    const creator = ruleCreatorWallet(rule);
    const mine = !!creator && creator === wallet;
    return {
      ruleId,
      objectId: null,
      objectName: null,
      canManageRule: mine,
      authoritySources: mine ? ['creator_legacy'] : [],
      reason: mine
        ? ''
        : creator
          ? 'This rule belongs to another wallet, and no spatial object links to it, '
            + 'so there is no on-chain authority to appeal to.'
          : 'This rule has no recorded creator and no spatial object links to it, so '
            + 'no authority can be established for it.',
      legacy: true,
      objectAuthorization: null,
    };
  }

  let auth: ObjectAuthorization | null = null;
  try {
    auth = await getObjectAuthorization(objectId, wallet);
  } catch (e) {
    return deny(
      'Authorization for the object this rule belongs to could not be read '
      + `(${e instanceof Error ? e.message : 'request failed'}), so managing it is `
      + 'unavailable rather than assumed allowed.',
    );
  }

  if (!auth) {
    return deny('Authorization for the object this rule belongs to could not be resolved.');
  }

  // The object exists in GeoLink but not in the spatial registry: there is
  // no chain authority to read, so this is the legacy case again.
  if (!auth.registered) {
    const creator = ruleCreatorWallet(rule);
    const mine = !!creator && creator === wallet;
    return {
      ruleId,
      objectId,
      objectName,
      canManageRule: mine,
      authoritySources: mine ? ['creator_legacy'] : [],
      reason: mine
        ? ''
        : `${objectName || 'The object'} this rule belongs to is not registered in the `
          + 'spatial registry, so authority falls back to the rule\'s creator — which '
          + 'is not this wallet.',
      legacy: true,
      objectAuthorization: auth,
    };
  }

  if (auth.canManageRules) {
    return {
      ruleId,
      objectId,
      objectName,
      canManageRule: true,
      authoritySources: auth.authoritySources,
      reason: '',
      legacy: false,
      objectAuthorization: auth,
    };
  }

  return {
    ruleId,
    objectId,
    objectName,
    canManageRule: false,
    authoritySources: auth.authoritySources,
    reason: describeRuleRefusal(auth, objectName),
    legacy: false,
    objectAuthorization: auth,
  };
}

/**
 * Name the specific precondition that failed, in the order the contract
 * checks them, so the first thing to go and fix is the thing reported.
 */
function describeRuleRefusal(auth: ObjectAuthorization, objectName: string | null): string {
  const label = objectName || 'the object this rule belongs to';
  const ctx = auth.context;

  if (ctx.statusName !== 'active') {
    return `${label} is ${ctx.statusName}. Execution rules cannot be changed until it is active again.`;
  }
  if (!ctx.effectiveOrganizationId) {
    return `${label} has no approved organization binding, so no wallet holds authority over its rules.`;
  }
  const binding = ctx.binding?.statusName;
  if (!ctx.inherited && binding && binding !== 'approved') {
    return `The organization binding on ${label} is ${binding} rather than approved, so rule `
      + 'authority does not resolve yet.';
  }
  const org = ctx.organization;
  if (org && org.statusName !== 'active') {
    return `The organization that owns ${label} is ${org.statusName}, so its authority over `
      + 'the object\'s rules is withdrawn.';
  }
  if (org && !org.capabilities.canOperateObjects) {
    return `The organization that owns ${label} is not cleared to operate objects `
      + '(authority verification withdrawn), so nobody inherits rule authority through it.';
  }
  return auth.reasons.canManageRules
    || `This wallet has no MANAGE_RULES authority over ${label}.`;
}

// ── Many rules at once ───────────────────────────────────────────────────────

/**
 * Authorization for a list of rules.
 *
 * Bounded concurrency because each unlinked rule is one GeoLink query and
 * each linked one is that plus a dozen contract simulations. A rule list of
 * fifty resolved all at once would be a self-inflicted load spike.
 */
export async function getRuleAuthorizations(
  rules: RuleOwnershipFields[],
  wallet: string | null | undefined,
): Promise<Map<string, RuleAuthorization>> {
  const out = new Map<string, RuleAuthorization>();
  const CONCURRENCY = 4;

  for (let i = 0; i < rules.length; i += CONCURRENCY) {
    const chunk = rules.slice(i, i + CONCURRENCY);
    const settled = await Promise.all(
      chunk.map((rule) =>
        getRuleAuthorization(rule, wallet).catch(
          (): RuleAuthorization => ({
            ruleId: String(rule.id),
            objectId: null,
            objectName: null,
            canManageRule: false,
            authoritySources: [],
            reason: 'Authorization could not be resolved for this rule.',
            legacy: false,
            objectAuthorization: null,
          }),
        ),
      ),
    );
    settled.forEach((auth) => out.set(auth.ruleId, auth));
  }
  return out;
}
